import React, { useEffect, useMemo, useState } from 'react';
import {
  HbrButton,
  HbrCard,
  HbrIcon,
  HbrInput,
  HbrMenu,
  HbrMenuItem,
  HbrDropdown,
  HbrSpinner,
  HbrTag,
} from '@harbor/elements-react-standalone';
import { checkCircle, info, magnifyingGlass, user, users } from '@harbor/elements/icons';
import { useMutation, useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems, postItem, putItem } from '../../../support/queries';

type UserLike = {
  id: string | number;
  name?: string;
  full_name?: string;
  email?: string;
  title?: string;
};

type TaskSubmission = {
  id?: string | number;
  assignment?: string | number | null;
  journey_map_item?: string | number | null;
  user?: string | number | null;
  target_kind?: 'internal_user' | 'external_email' | string | null;
  target_user?: string | number | UserLike | null;
  target_email?: string | null;
  notes?: string | null;
  created?: string | null;
  last_updated?: string | null;
};

type Props = {
  item: any;
  assignmentId?: string | number | null;
  currentUserId?: string | number | null;
  existingSubmission?: TaskSubmission | null;
  disabled?: boolean;
  onSaved?: (submission: any) => void;
};

const getUserLabel = (u: UserLike | null | undefined) => {
  if (!u) return '';
  return u.full_name || u.name || u.email || `User ${u.id}`;
};

const normalizeExistingTargetKind = (submission?: TaskSubmission | null) => {
  const raw = String(submission?.target_kind || '').trim().toLowerCase();
  if (raw === 'internal_user') return 'internal_user';
  if (raw === 'external_email') return 'external_email';
  return 'internal_user';
};

const isValidEmail = (value: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
};

const JourneyMapTaskSubmissionForm: React.FC<Props> = ({
                                                         item,
                                                         assignmentId = null,
                                                         currentUserId = null,
                                                         existingSubmission = null,
                                                         disabled = false,
                                                         onSaved,
                                                       }) => {
  const metadata = useMemo(() => {
    if (!item?.metadata) return {};
    if (typeof item.metadata === 'string') {
      try {
        return JSON.parse(item.metadata);
      } catch {
        return {};
      }
    }
    return typeof item.metadata === 'object' ? item.metadata : {};
  }, [item]);

  const allowsInternal =
    item?.allows_internal_shadow_target === true ||
    metadata?.allow_internal_user_select === true;

  const allowsExternal =
    item?.allow_external_shadow_target === true ||
    metadata?.allow_external_email === true;

  const notesRequired = metadata?.notes_required === true;
  const instructions = String(metadata?.instructions || '').trim();

  const [targetKind, setTargetKind] = useState<'internal_user' | 'external_email'>(
    normalizeExistingTargetKind(existingSubmission)
  );
  const [targetUser, setTargetUser] = useState<UserLike | null>(
    typeof existingSubmission?.target_user === 'object' && existingSubmission?.target_user
      ? (existingSubmission.target_user as UserLike)
      : null
  );
  const [targetUserId, setTargetUserId] = useState<string>(
    typeof existingSubmission?.target_user === 'object'
      ? String(existingSubmission?.target_user?.id || '')
      : existingSubmission?.target_user != null
        ? String(existingSubmission.target_user)
        : ''
  );
  const [targetEmail, setTargetEmail] = useState<string>(String(existingSubmission?.target_email || ''));
  const [notes, setNotes] = useState<string>(String(existingSubmission?.notes || ''));
  const [search, setSearch] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowsInternal && allowsExternal) {
      setTargetKind('external_email');
    } else if (allowsInternal && !allowsExternal) {
      setTargetKind('internal_user');
    }
  }, [allowsInternal, allowsExternal]);

  const usersQuery = useQuery({
    queryKey: ['journey-map-task-users', search],
    queryFn: async () => {
      const trimmed = search.trim();
      const rows = await fetchItems(
        'db/user/',
        trimmed ? { search: trimmed } : {},
        { includePageSize: false }
      ).catch(() => []);
      return Array.isArray(rows) ? rows : [];
    },
    ...{
        enabled: allowsInternal,
        staleTime: 60_000,
      },
  });

  const filteredUsers = useMemo(() => {
    const rows = Array.isArray(usersQuery.data) ? usersQuery.data : [];
    const q = search.trim().toLowerCase();
    if (!q) return rows.slice(0, 20);

    return rows
      .filter((u: any) => {
        const label = getUserLabel(u).toLowerCase();
        const email = String(u?.email || '').toLowerCase();
        const title = String(u?.title || '').toLowerCase();
        return label.includes(q) || email.includes(q) || title.includes(q);
      })
      .slice(0, 20);
  }, [usersQuery.data, search]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      setFormError(null);

      if (!item?.id) {
        throw new Error('Missing journey map item id.');
      }

      if (!assignmentId) {
        throw new Error('Missing assignment id.');
      }

      if (targetKind === 'internal_user') {
        if (!allowsInternal) {
          throw new Error('Internal user selection is not allowed for this task.');
        }
        if (!targetUserId) {
          throw new Error('Please select an internal user.');
        }
      }

      if (targetKind === 'external_email') {
        if (!allowsExternal) {
          throw new Error('External email entry is not allowed for this task.');
        }
        if (!targetEmail.trim()) {
          throw new Error('Please enter an external email.');
        }
        if (!isValidEmail(targetEmail.trim())) {
          throw new Error('Please enter a valid email address.');
        }
      }

      if (notesRequired && !notes.trim()) {
        throw new Error('Notes are required for this task.');
      }

      const payload: Record<string, any> = {
        assignment: assignmentId,
        journey_map_item: item.id,
        user: currentUserId || null,
        target_kind: targetKind,
        target_user: targetKind === 'internal_user' ? Number(targetUserId) : null,
        target_email: targetKind === 'external_email' ? targetEmail.trim() : '',
        notes: notes.trim(),
      };

      if (existingSubmission?.id) {
        return await putItem('db/journey-map-task-submission', {
          id: existingSubmission.id,
          ...payload,
        });
      }

      return await postItem('db/journey-map-task-submission/', payload);
    },
    onSuccess: (data) => {
      onSaved?.(data);
    },
    onError: (error: any) => {
      const message =
        error?.message || error?.response?.data?.detail || 'Unable to save task submission.';
      setFormError(message);
    },
  });

  return (
    <div className="hbr-css__layout-col-md">
      <HbrCard
        style={
          {
            '--box-shadow': 'none',
            '--border-width': '2px',
          } as React.CSSProperties
        }
      >
        <div className="hbr-css__layout-col-md">
          <div className="hbr-css__layout-col-3xs">
            <div className="hbr-css__text-primary-sm-semibold">Shadowing Submission</div>
            <div className="hbr-css__text-secondary-sm">
              Submit who you shadowed. Completion will move to pending validation until confirmed.
            </div>
          </div>

          {instructions ? (
            <HbrCard
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                  '--border-color': 'var(--hbr-color-neutral-border-weak)',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__layout-row-3xs hbr-css__layout-align-start hbr-css__text-secondary-sm">
                <HbrIcon asset={info} />
                <div>{instructions}</div>
              </div>
            </HbrCard>
          ) : null}

          <div className="hbr-css__layout-row-sm hbr-css__layout-wrap">
            {allowsInternal ? (
              <HbrButton
                variant={targetKind === 'internal_user' ? 'fill' : 'outline'}
                onClick={() => setTargetKind('internal_user')}
                disabled={disabled}
              >
                <HbrIcon slot="prefix" asset={users} />
                Internal User
              </HbrButton>
            ) : null}

            {allowsExternal ? (
              <HbrButton
                variant={targetKind === 'external_email' ? 'fill' : 'outline'}
                onClick={() => setTargetKind('external_email')}
                disabled={disabled}
              >
                <HbrIcon slot="prefix" asset={user} />
                External Email
              </HbrButton>
            ) : null}
          </div>

          {targetKind === 'internal_user' && allowsInternal ? (
            <div className="hbr-css__layout-col-sm">
              <div className="hbr-css__text-secondary-xs">Search Internal User</div>

              <HbrInput
                value={search}
                placeholder="Search by name, email, or title"
                onHbr-input={(e: any) =>
                  setSearch(String(e?.target?.value ?? e?.detail?.value ?? ''))
                }
              >
                <HbrIcon slot="prefix" asset={magnifyingGlass} />
              </HbrInput>

              {targetUser ? (
                <HbrTag size="small">Selected: {getUserLabel(targetUser)}</HbrTag>
              ) : null}

              {usersQuery.isLoading ? (
                <HbrSpinner size="small">
                  <div slot="label">Loading users...</div>
                </HbrSpinner>
              ) : (
                <div
                  className="hbr-css__layout-col-xs"
                  style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: 4 }}
                >
                  {filteredUsers.map((u: any) => {
                    const selected = String(u.id) === String(targetUserId);
                    return (
                      <HbrCard
                        key={String(u.id)}
                        selectable
                        selected={selected}
                        role="button"
                        sentiment={selected ? 'info' : undefined}
                        onClick={() => {
                          setTargetUser(u);
                          setTargetUserId(String(u.id));
                        }}
                        style={
                          {
                            '--box-shadow': 'none',
                            '--border-width': '2px',
                          } as React.CSSProperties
                        }
                      >
                        <div className="hbr-css__layout-col-3xs">
                          <div className="hbr-css__text-primary-sm-semibold">{getUserLabel(u)}</div>
                          {u?.email ? (
                            <div className="hbr-css__text-secondary-xs">{u.email}</div>
                          ) : null}
                          {u?.title ? (
                            <div className="hbr-css__text-secondary-xs">{u.title}</div>
                          ) : null}
                        </div>
                      </HbrCard>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          {targetKind === 'external_email' && allowsExternal ? (
            <div className="hbr-css__layout-col-sm">
              <div className="hbr-css__text-secondary-xs">External Email</div>
              <HbrInput
                value={targetEmail}
                placeholder="name@example.com"
                onHbr-input={(e: any) =>
                  setTargetEmail(String(e?.target?.value ?? e?.detail?.value ?? ''))
                }
              />
            </div>
          ) : null}

          <div className="hbr-css__layout-col-sm">
            <div className="hbr-css__layout-row-xs hbr-css__layout-align-center">
              <div className="hbr-css__text-secondary-xs">Notes</div>
              {notesRequired ? <HbrTag size="small">Required</HbrTag> : null}
            </div>

            <textarea
              value={notes}
              disabled={disabled}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              placeholder="Add any context, agenda, or proof details here."
              style={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid var(--hbr-color-neutral-border-weak)',
                background: 'var(--hbr-color-neutral-bg-weakest)',
                color: 'var(--hbr-color-neutral-text)',
                padding: '12px',
                font: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>

          {existingSubmission?.id ? (
            <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
              <HbrTag size="small">
                <HbrIcon slot="prefix" asset={checkCircle} />
                Existing submission loaded
              </HbrTag>
            </div>
          ) : null}

          {formError ? (
            <HbrCard
              sentiment="danger"
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__text-secondary-sm">{formError}</div>
            </HbrCard>
          ) : null}

          <div>
            <HbrButton
              onClick={() => saveMutation.mutate()}
              disabled={disabled || saveMutation.isPending}
            >
              {saveMutation.isPending
                ? 'Saving...'
                : existingSubmission?.id
                  ? 'Update Submission'
                  : 'Submit Shadowing Task'}
            </HbrButton>
          </div>
        </div>
      </HbrCard>
    </div>
  );
};

export default JourneyMapTaskSubmissionForm;